import { Button, Icon, ProjectTaskSelect, SingleSelect, TaskCategoryIcon } from '~/components';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';

const directionLabels = {
  predecessor: 'Blocked By',
  successor: 'Blocking',
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RowMain = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding-left: 0.875rem;
`;

const RowName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowLabel = styled.span`
  color: ${colors.grey55};
  flex-shrink: 0;
  width: 9rem;
  padding-left: 1rem;
`;

const RowRemove = styled(Button)`
  color: ${colors.danger};
  margin-left: 0.5rem;
  flex-shrink: 0;

  &:hover {
    color: ${colors.danger};
    opacity: 0.7;
  }
`;

const Composer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
`;

const ComposerDirection = styled.div`
  width: 9rem;
  flex-shrink: 0;
`;

const ComposerSelect = styled.div`
  flex: 1;
  min-width: 0;
`;

function ProjectTaskRelationships({ project, taskId, predecessors, successors, onChange, disabled = false }) {
  const [composerDirection, setComposerDirection] = useState('predecessor');

  const rows = useMemo(
    () => [
      ...predecessors.map((t) => ({
        id: t.id,
        name: t.name,
        statusId: t.statusId,
        taskCategory: t.taskCategory,
        direction: 'predecessor',
      })),
      ...successors.map((t) => ({
        id: t.id,
        name: t.name,
        statusId: t.statusId,
        taskCategory: t.taskCategory,
        direction: 'successor',
      })),
    ],
    [predecessors, successors],
  );

  const excludedTaskIds = useMemo(() => [taskId, ...rows.map((r) => r.id)].filter(Boolean), [taskId, rows]);

  const emit = (nextRows) => {
    onChange({
      predecessors: nextRows
        .filter((r) => r.direction === 'predecessor')
        .map(({ id, name, statusId, taskCategory }) => ({ id, name, statusId, taskCategory })),
      successors: nextRows
        .filter((r) => r.direction === 'successor')
        .map(({ id, name, statusId, taskCategory }) => ({ id, name, statusId, taskCategory })),
    });
  };

  const handleRemove = (index) => {
    emit(rows.filter((_, i) => i !== index));
  };

  const handleAdd = ({ target: { value: task } }) => {
    if (!task) return;
    emit([
      ...rows,
      {
        id: task.id,
        name: task.name,
        statusId: task.statusId,
        taskCategory: task.taskCategory,
        direction: composerDirection,
      },
    ]);
  };

  return (
    <Container>
      {rows.length > 0 && (
        <List>
          {rows.map((row, index) => (
            <Row key={`${row.direction}-${row.id}`}>
              <RowLabel>{directionLabels[row.direction]}</RowLabel>
              <RowMain>
                {row.taskCategory && <TaskCategoryIcon icon={row.taskCategory.icon} color={row.taskCategory.color} />}
                <RowName>{row.name}</RowName>
                {!disabled && (
                  <RowRemove isAnchor title="Remove" onClick={() => handleRemove(index)}>
                    <Icon icon="times" />
                  </RowRemove>
                )}
              </RowMain>
            </Row>
          ))}
        </List>
      )}

      {!disabled && (
        <Composer>
          <ComposerDirection>
            <SingleSelect value={composerDirection} onChange={({ target: { value } }) => setComposerDirection(value)}>
              {Object.entries(directionLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SingleSelect>
          </ComposerDirection>
          <ComposerSelect>
            <ProjectTaskSelect
              value={null}
              project={project}
              exclude={excludedTaskIds}
              disableRollups
              placeholder="Task"
              onChange={handleAdd}
            />
          </ComposerSelect>
        </Composer>
      )}
    </Container>
  );
}

export default ProjectTaskRelationships;
